import React, { useState, useEffect, useCallback } from "react";
import DayShiftCard from "./DayShiftCard";
import ShiftFormModal from "./ShiftFormModal";
import Spinner from "../../components/Loader";
import {
  DayOfWeek,
  ShiftConfigPayload,
  WeekShiftConfig,
} from "../../util/types";
// import * as api from "../../services/api";
// import logger from "../../utils/logger";
import apiService from "services/apiService";

const ShiftConfigurationPage: React.FC = () => {
  const [weekConfig, setWeekConfig] = useState<WeekShiftConfig>({});
  const [originalWeekConfig, setOriginalWeekConfig] = useState<WeekShiftConfig>(
    {},
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<DayOfWeek>("Mon");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const daysOfWeek: DayOfWeek[] = [
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
    "Sun",
  ];

  const fetchWeekConfig = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      // const data = await api.getWeekShiftConfig();
      const response = await apiService.get("api/roster/shift-config/week");
      console.log("Fetched shift config:", response);

      // Transform API response: convert full day names to abbreviated names
      const apiData = response.data;
      const transformedData: WeekShiftConfig = {};

      const dayMapping: { [key: string]: DayOfWeek } = {
        Monday: "Mon",
        Tuesday: "Tue",
        Wednesday: "Wed",
        Thursday: "Thu",
        Friday: "Fri",
        Saturday: "Sat",
        Sunday: "Sun",
      };

      Object.keys(apiData).forEach((fullDayName) => {
        const abbreviatedDay = dayMapping[fullDayName];
        if (abbreviatedDay) {
          transformedData[abbreviatedDay] = apiData[fullDayName];
        }
      });

      console.log("Transformed shift config data:", transformedData);
      setWeekConfig(transformedData);
      setOriginalWeekConfig(transformedData);
      setHasUnsavedChanges(false);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch shift configuration.",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWeekConfig();
  }, [fetchWeekConfig]);

  const handleConfigureDay = (day: DayOfWeek) => {
    setSelectedDay(day);
    setIsModalOpen(true);
  };

  const handleUpdateDayShifts = (
    shifts: ShiftConfigPayload[],
    applySameForWholeWeek: boolean,
  ) => {
    if (applySameForWholeWeek) {
      // Apply to all days
      const updatedConfig: WeekShiftConfig = {};
      daysOfWeek.forEach((day) => {
        updatedConfig[day] = shifts.map((shift) => ({ ...shift, day }));
      });
      setWeekConfig(updatedConfig);
      setHasUnsavedChanges(true);
    } else {
      // Apply to selected day only
      setWeekConfig({
        ...weekConfig,
        [selectedDay]: shifts.map((shift) => ({ ...shift, day: selectedDay })),
      });
      setHasUnsavedChanges(true);
    }
  };

  const handleSaveAllChanges = async () => {
    try {
      setIsSaving(true);
      setError(null);

      // Mapping from abbreviated to full day names
      const dayMapping: { [key in DayOfWeek]: string } = {
        Mon: "Monday",
        Tue: "Tuesday",
        Wed: "Wednesday",
        Thu: "Thursday",
        Fri: "Friday",
        Sat: "Saturday",
        Sun: "Sunday",
      };

      // Determine which days have changed
      const changedDays = daysOfWeek.filter((day) => {
        const current = weekConfig[day] || [];
        const original = originalWeekConfig[day] || [];
        return JSON.stringify(current) !== JSON.stringify(original);
      });

      if (changedDays.length === 0) {
        // logger.info("USER_ACTION", "Save shifts clicked with no changes");
        setSuccessMessage("No changes to save");
        setTimeout(() => setSuccessMessage(null), 2000);
        setIsSaving(false);
        return;
      }

      // logger.userAction("Save Shift Configuration", {
      //   changedDays,
      //   totalShifts: changedDays.reduce((sum, day) => sum + (weekConfig[day]?.length || 0), 0),
      // });

      // Make API calls for each changed day with new format
      const savePromises = changedDays.map((day) => {
        const fullDayName = dayMapping[day];
        const shifts = (weekConfig[day] || []).map((shift) => ({
          ...shift,
          day: fullDayName,
        }));

        return apiService.post("api/roster/shift-config", {
          applySameForWholeWeek: false,
          day: fullDayName,
          shifts: shifts,
        });
      });

      const responses = await Promise.all(savePromises);
      console.log("Save responses:", responses);

      // Show success message
      setSuccessMessage(
        `Shift configurations saved successfully! (${changedDays.length} day${
          changedDays.length !== 1 ? "s" : ""
        } updated)`,
      );
      setTimeout(() => setSuccessMessage(null), 3000);

      // Update original config and clear unsaved changes
      setOriginalWeekConfig(weekConfig);
      setHasUnsavedChanges(false);

      // logger.info(
      //   "USER_ACTION",
      //   `Shift configurations saved for ${changedDays.length} days`,
      // );
    } catch (err) {
      // logger.error("USER_ACTION", "Failed to save shift configuration", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to save shift configuration",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    setWeekConfig(originalWeekConfig);
    setHasUnsavedChanges(false);
    setSuccessMessage("Changes discarded");
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  const handleDeleteAll = async () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete ALL shift configurations for the entire week?\n\nThis action cannot be undone.",
    );

    if (!confirmDelete) return;

    try {
      setIsDeleting(true);
      setError(null);

      const response = await apiService.delete(
        "api/roster/shift-config/deleteAll",
      );
      console.log("Delete all response:", response);

      // Clear local state
      setWeekConfig({});
      setOriginalWeekConfig({});
      setHasUnsavedChanges(false);

      setSuccessMessage("All shift configurations deleted successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to delete shift configurations",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteDay = async (day: DayOfWeek) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete shift configuration for ${day}?\n\nThis action cannot be undone.`,
    );

    if (!confirmDelete) return;

    // Mapping from abbreviated to full day names
    const dayMapping: { [key in DayOfWeek]: string } = {
      Mon: "Monday",
      Tue: "Tuesday",
      Wed: "Wednesday",
      Thu: "Thursday",
      Fri: "Friday",
      Sat: "Saturday",
      Sun: "Sunday",
    };

    try {
      setIsDeleting(true);
      setError(null);

      const fullDayName = dayMapping[day];
      const response = await apiService.delete(
        `api/roster/shift-config/deleteDay/${fullDayName}`,
      );
      console.log("Delete day response:", response);

      // Update local state - remove the day's configuration
      const updatedWeekConfig = { ...weekConfig };
      delete updatedWeekConfig[day];
      setWeekConfig(updatedWeekConfig);

      const updatedOriginalConfig = { ...originalWeekConfig };
      delete updatedOriginalConfig[day];
      setOriginalWeekConfig(updatedOriginalConfig);

      setSuccessMessage(`Shift configuration for ${day} deleted successfully!`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Failed to delete ${day} configuration`,
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const getTotalShiftsAndAgents = () => {
    let totalShifts = 0;
    let totalAgents = 0;
    Object.values(weekConfig).forEach((shifts: ShiftConfigPayload[]) => {
      totalShifts += shifts.length;
      totalAgents += shifts.reduce(
        (sum, shift) => sum + shift.requiredAgents,
        0,
      );
    });
    return { totalShifts, totalAgents };
  };

  const { totalShifts, totalAgents } = getTotalShiftsAndAgents();
  console.log("Rendering ShiftConfigurationPage:", {
    weekConfig,
    totalShifts,
    totalAgents,
  });

  return (
    <div className="pl-2 w-full h-page-content">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-1">
            Shift Configuration
          </h1>
          <p className="text-base text-gray-600 dark:text-gray-400">
            Configure shift schedules for each day of the week
          </p>
        </div>

        <div className="flex gap-2 mt-2 pr-2">
          {/* Delete All Button - Show when there are configurations */}
          {totalShifts > 0 && !hasUnsavedChanges && (
            <button
              onClick={handleDeleteAll}
              disabled={isDeleting || isSaving}
              className="bg-red-600 text-white text-sm font-semibold px-4 py-1.5 rounded-lg shadow-md hover:bg-red-700 transition-colors duration-200 disabled:bg-red-400 disabled:cursor-not-allowed flex items-center"
            >
              {isDeleting && (
                <svg
                  className="animate-spin -ml-1 mr-2 h-3 w-3 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              {isDeleting ? "Deleting..." : "Delete All"}
            </button>
          )}

          {/* Save/Discard Buttons */}
          {hasUnsavedChanges && (
            <>
              <button
                onClick={handleDiscardChanges}
                disabled={isSaving}
                className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold px-4 py-1.5 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200 disabled:opacity-50"
              >
                Discard
              </button>
              <button
                onClick={handleSaveAllChanges}
                disabled={isSaving}
                className="bg-indigo-600 text-white text-sm font-semibold px-4 py-1.5 rounded-lg shadow-md hover:bg-indigo-700 transition-colors duration-200 disabled:bg-indigo-400 disabled:cursor-not-allowed flex items-center"
              >
                {isSaving && (
                  <svg
                    className="animate-spin -ml-1 mr-2 h-3 w-3 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Unsaved Changes Banner */}
      {hasUnsavedChanges && (
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200 px-3 py-2 rounded-lg mb-3 flex items-center text-base">
          <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          You have unsaved changes. Please save to apply them.
        </div>
      )}

      {successMessage && (
        <div className="bg-green-100 dark:bg-green-900/30 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-200 px-3 py-2 rounded-lg mb-3 flex items-center text-base">
          <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          {successMessage}
        </div>
      )}

      {error && (
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-3 py-2 rounded-lg mb-3 text-base">
          {error}
        </div>
      )}

      {/* Summary Stats */}
      {!isLoading && totalShifts > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-lg p-3 shadow-md">
            <div className="text-sm opacity-90">Total Shifts</div>
            <div className="text-2xl font-bold">
              {totalShifts}
            </div>
          </div>
          <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-3 shadow-md">
            <div className="text-sm opacity-90">
              Total Agents Needed
            </div>
            <div className="text-2xl font-bold">
              {totalAgents}
            </div>
          </div>
          {/* <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg p-3 shadow-md"> */}
          <div className="bg-gradient-to-r from-gray-500 to-gray-600 dark:from-gray-600 dark:to-gray-700 text-white rounded-lg p-3 shadow-md">
            <div className="text-sm opacity-90">
              Days Configured
            </div>
            <div className="text-2xl font-bold">
              {
                Object.keys(weekConfig).filter(
                  (day) => weekConfig[day]?.length > 0,
                ).length
              }
              /7
            </div>
          </div>
        </div>
      )}

      {isLoading && <Spinner />}

      {!isLoading && (
        <>
          {totalShifts === 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <svg
                  className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <h3 className="text-base font-semibold text-blue-900 dark:text-blue-200 mb-1">
                    Get Started
                  </h3>
                  <p className="text-blue-700 dark:text-blue-300 mb-2 text-base">
                    No shift configurations found. Click on any day card below
                    to configure shifts for that day.
                  </p>
                  <p className="text-blue-600 dark:text-blue-400 text-sm">
                    💡 <strong>Tip:</strong> You can apply the same shift
                    configuration to the entire week by checking when
                    configuring.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
            {daysOfWeek.map((day) => (
              <DayShiftCard
                key={day}
                day={day}
                shifts={weekConfig[day] || []}
                onConfigure={handleConfigureDay}
                onDelete={handleDeleteDay}
                hasUnsavedChanges={hasUnsavedChanges}
              />
            ))}
          </div>
        </>
      )}

      <ShiftFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpdate={handleUpdateDayShifts}
        day={selectedDay}
        existingShifts={weekConfig[selectedDay] || []}
      />
    </div>
  );
};

export default ShiftConfigurationPage;
